import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Paper,
  useMediaQuery,
  Chip,
  Skeleton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { useStyles } from "../../assets/styles/Theme";
import Tooltip from "@mui/material/Tooltip";

function ViewDataGridGrnApprover({
  columns = [],
  rows = [],
  menuItems = [],
  tabletitle = "",
  gridid = "",
  displayActions = true,
  onSelectionChange,
  grnStatusTypes,
  grnReturnStatusTypes,
  selectionModel,
  sx,
    clickableGrn = false,
    showCheckbox = true,
     onGrnClick,
  loading = false,
  initialSortModel,
  showSkeletonWhenEmpty = false,
    getMenuItems,     
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const density = "standard";
  const theme = useTheme();
  const styles = useStyles(theme);
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedRows, setSelectedRows] = useState([]);

  const handleOpenUserMenu = (event, row) => {
    setAnchorEl({ anchor: event.currentTarget, row });
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (menuItem, row) => {
    // menuItem.onClick(row[gridid]);
    menuItem.onClick(row);
    handleCloseUserMenu();
  };

  if (!columns || !rows || !Array.isArray(columns) || !Array.isArray(rows)) {
    console.error("Invalid columns or rows");
    return null;
  }

  // Convert table titles to title case
  const titleCase = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };


const GRN_BREADCRUMBS = {
  STATUS_FALLBACK: "Unknown",
};

const GRN_INITIATOR_FILTER = {
  STATUS_FALLBACK_COLOR: "#999",
};

const isApproved = (trcatcode) => String(trcatcode) === "2";
 // Add action column if displayActions is true

const getStatusLabelAndColor = (trcatcode, matchedStatus) => {
  const approved = isApproved(trcatcode);
  return {
    statusLabel: approved ? "Approved" : matchedStatus?.trappuserremark || GRN_BREADCRUMBS.STATUS_FALLBACK,
    statusColor: approved ? "#28A745" : matchedStatus?.colorcode || GRN_INITIATOR_FILTER.STATUS_FALLBACK_COLOR,
  };
};

  const actionColumn = displayActions
    ? [
        {
          field: "actions",
          headerName: "Actions",
          width: 100,
          headerAlign: "center",
          align: "center",
          // position: "fixed",
          renderCell: (params) => (
            <IconButton
              onClick={(event) => handleOpenUserMenu(event, params.row)}
            >
              <MoreVertIcon />
            </IconButton>
          ),
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          pinned: "right",
          cellClassName: "actions-column-cell",
          headerClassName: "actions-column-header",
        },
      ]
    : [];

const renderTrstsCell = (params, grnStatusTypes, grnReturnStatusTypes) => {
  // ✅ Use trcatcode from the row to decide approved, not trsts
  const trcatcode = params.row.trcatcode;

  const matchedStatus =
    grnStatusTypes?.find((s) => String(s.trsts) === String(params.value)) ||
    grnReturnStatusTypes?.find((s) => String(s.trsts) === String(params.value));

  const { statusLabel, statusColor } = getStatusLabelAndColor(trcatcode, matchedStatus);

  return (
    <Tooltip title={statusLabel}>
      <Chip label={statusLabel} size="small" sx={{ backgroundColor: statusColor, color: "#fff" }} />
    </Tooltip>
  );
};

const renderActionTypeCell = (params) => {
  const value = String(params.value ?? params.row?.trcatcode ?? "").trim();
  const isGrn = value.toLowerCase() === "grn";

  return (
    <Chip
      label={value || "Unknown"}
      size="small"
      sx={{
        backgroundColor: isGrn ? "#2e7d32" : "#8d4a0f",
        color: "#fff",
      
       
      }}
    />
  );
};

const dataGridColumns = columns.map((column) => ({
  ...column,
  headerName: titleCase(column.headerName),

  headerAlign:
    column.field === "totaftertaxamount"
      ? "right"
      : column.textAlign
      ? column.textAlign.toLowerCase()
      : "left",

  align:
    column.field === "totaftertaxamount"
      ? "right"
      : column.textAlign
      ? column.textAlign.toLowerCase()
      : "left",

  renderCell:
    column.field === "grncode"
      ? (params) => {
          if (!clickableGrn) {
            return params.value || "";
          }

          return (
            <Tooltip title={params.value}>
              <span
                style={{
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();

                  if (onGrnClick) {
                    onGrnClick(params.row);
                    return;
                  }

                  const approvedMenu = menuItems.find(
                    (item) =>
                      item.text &&
                      item.text.toLowerCase().includes("return")
                  );

                  approvedMenu?.onClick?.(params.row);
                }}
              >
                {params.value}
              </span>
            </Tooltip>
          );
        }
      : column.field === "trsts"
      ? (params) => renderTrstsCell(params, grnStatusTypes, grnReturnStatusTypes)
      : column.field === "trcatcode"
      ? (params) => renderActionTypeCell(params)
      : ["suptypecode", "lastupdateuser"].includes(column.field)
      ? (params) => params.value || ""
      : undefined,
}));

  const renderGridSkeletonPlaceholder = () => {
    const skeletonColumnCount = Math.max(Math.min(columns?.length ?? 0, 6), 4);
    const placeholderColumns = Array.from({ length: skeletonColumnCount });
    const placeholderRows = Array.from({ length: isMediumScreen ? 5 : 8 });
    const widthCycle = ["82%", "60%", "74%", "55%", "92%", "68%"];
    const gridTemplateColumns = `36px repeat(${Math.max(
      skeletonColumnCount,
      1
    )}, minmax(0, 1fr)) 60px`;
    const baseSkeletonSx = {
      bgcolor: alpha(theme.palette.text.primary, 0.1),
      transformOrigin: "left",
    };
    const accentDivider = alpha(theme.palette.divider, 0.35);
    const topBarBg = alpha(theme.palette.primary.light, 0.18);

    return (
      <Box sx={{ p: isMediumScreen ? 1.5 : 3 }}>
        <Box
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${accentDivider}`,
            boxShadow: `0 24px 60px ${alpha(theme.palette.common.black, 0.08)}`,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.95
            )}, ${alpha(theme.palette.background.default, 0.9)})`,
          }}
        >
          <Box
            sx={{
              px: isMediumScreen ? 2 : 3,
              py: isMediumScreen ? 1.5 : 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              background: `linear-gradient(120deg, ${topBarBg}, transparent)`,
              borderBottom: `1px solid ${accentDivider}`,
            }}
          >
            <Skeleton
              variant="rounded"
              animation="wave"
              height={34}
              width={isMediumScreen ? "65%" : "35%"}
              sx={{ ...baseSkeletonSx, borderRadius: 2 }}
            />
            <Box sx={{ display: "flex", gap: 1.2 }}>
              <Skeleton
                variant="rounded"
                animation="wave"
                height={30}
                width={isMediumScreen ? 80 : 120}
                sx={baseSkeletonSx}
              />
              <Skeleton
                variant="rounded"
                animation="wave"
                height={30}
                width={isMediumScreen ? 60 : 90}
                sx={baseSkeletonSx}
              />
              <Skeleton
                variant="circular"
                animation="wave"
                height={30}
                width={30}
                sx={{ ...baseSkeletonSx, bgcolor: alpha(theme.palette.primary.main, 0.25) }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              gap: isMediumScreen ? 1 : 1.5,
              px: isMediumScreen ? 1.5 : 2.5,
              py: 1.4,
              alignItems: "center",
              borderBottom: `1px solid ${accentDivider}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Skeleton
              variant="circular"
              animation="wave"
              width={18}
              height={18}
              sx={{ ...baseSkeletonSx, bgcolor: alpha(theme.palette.primary.main, 0.25) }}
            />
            {placeholderColumns.map((_, index) => (
              <Skeleton
                key={`header-placeholder-${index}`}
                variant="rounded"
                animation="wave"
                height={14}
                sx={{
                  ...baseSkeletonSx,
                  width: widthCycle[index % widthCycle.length],
                }}
              />
            ))}
            <Skeleton
              variant="rounded"
              animation="wave"
              width={42}
              height={14}
              sx={baseSkeletonSx}
            />
          </Box>
          {placeholderRows.map((_, rowIndex) => (
            <Box
              key={`row-placeholder-${rowIndex}`}
              sx={{
                display: "grid",
                gridTemplateColumns,
                gap: isMediumScreen ? 1 : 1.5,
                px: isMediumScreen ? 1.5 : 2.5,
                py: 1.5,
                alignItems: "center",
                backgroundColor:
                  rowIndex % 2 === 0
                    ? alpha(theme.palette.primary.main, 0.015)
                    : alpha(theme.palette.background.paper, 0.6),
                borderBottom:
                  rowIndex === placeholderRows.length - 1
                    ? "none"
                    : `1px dashed ${alpha(theme.palette.divider, 0.35)}`,
              }}
            >
              <Skeleton
                variant="circular"
                animation="wave"
                width={18}
                height={18}
                sx={{ ...baseSkeletonSx, bgcolor: alpha(theme.palette.primary.main, 0.2) }}
              />
              {placeholderColumns.map((_, columnIndex) => (
                <Skeleton
                  key={`cell-placeholder-${rowIndex}-${columnIndex}`}
                  variant="rounded"
                  animation="wave"
                  height={16}
                  sx={{
                    ...baseSkeletonSx,
                    width:
                      widthCycle[(rowIndex + columnIndex) % widthCycle.length],
                  }}
                />
              ))}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Skeleton
                  variant="circular"
                  animation="wave"
                  width={28}
                  height={28}
                  sx={{ ...baseSkeletonSx, bgcolor: alpha(theme.palette.text.primary, 0.15) }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };
  return (
    <>
      <Paper sx={{ ...styles.VDG_paper, ...sx }}>
        {/* <Box sx={styles.VDG_box}> */}

        {/* Row Density Dropdown */}

        {}
        <Box sx={{ height: 480, width: "100%" }}>
          {loading || (showSkeletonWhenEmpty && rows.length === 0) ? (
            renderGridSkeletonPlaceholder()
          ) : (
          <DataGrid
            columns={[...dataGridColumns, ...actionColumn]}
            rows={rows}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 100]}
           checkboxSelection={showCheckbox}
            disableSelectionOnClick
            density={density}
            // onRowSelectionModelChange={(newSelection) => {
            //   setSelectedRows(newSelection);
            // }}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
              onSelectionChange?.(newSelection); // ← propagate up
            }}
            // rowSelectionModel={selectedRows}
            rowSelectionModel={selectionModel ?? selectedRows}
            getRowId={(row) => row.id}
            {...(initialSortModel && { sortModel: initialSortModel })}
            components={{
              ColumnMenu: null,
            }}
            sx={{
              fontSize: theme.typography.body_s,
              p: 1,
              overflowX: "auto",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.paper,
                textTransform: "capitalize",
              },
              // "& .MuiDataGrid-cell": {
              //   display: "flex",
              //   alignItems: "center",
              //   justifyContent: "center",
              // },
              "& .MuiDataGrid-columnHeaderTitle": {
                textAlign: "center",
                width: "100%",
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle_m,
                fontWeight: 600,
              },
              "& .MuiDataGrid-columnHeader--pinnedRight": {
                backgroundColor: theme.palette.background.paper,
              },
              "& .MuiDataGrid-cell--pinnedRight": {
                backgroundColor: theme.palette.background.paper,
              },

              "& .MuiDataGrid-container--top  [role=row]": {
                backgroundColor: theme.palette.background.bgShade_1,
                color: theme.palette.text.secondary,

                // boxShadow: 1,
              },
              "& .actions-column-cell": {
                position: "sticky",
                right: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
                // borderLeft: `1px solid ${theme.palette.divider}`,
              },
              "& .actions-column-header": {
                position: "sticky",
                right: 0,
                backgroundColor: theme.palette.background.bgShade_1,
                zIndex: 2,
                // borderLeft: `1px solid ${theme.palette.divider}`,
              },
            }}
          />
          )}
        </Box>
        {/* </Box> */}
        <Menu
          anchorEl={anchorEl?.anchor}
          open={Boolean(anchorEl)}
          onClose={handleCloseUserMenu}
          sx={{ ...styles.VDG_colActionIconMenu }}
        >
          {menuItems.map((menuItem, index) => {
            // Safely get row
            const row = anchorEl?.row;

            // Determine if disabled for this row, only if row exists
            const isDisabled =
              typeof menuItem.disabled === "function" && row
                ? menuItem.disabled(row)
                : menuItem.disabled || false;

            return (
              <MenuItem
                key={index}
                onClick={() => {
                  if (!isDisabled) {
                    handleMenuItemClick(menuItem, row);
                  }
                }}
                sx={{ ...styles.VDG_colActionIcon }}
                disabled={isDisabled}
              >
                {menuItem.icon}
                <span className="menuItemText">{menuItem.text}</span>
              </MenuItem>
            );
          })}
        </Menu>

{/* New Menu - only used when getMenuItems is provided */}
{getMenuItems && (
  <Menu
    anchorEl={anchorEl?.anchor}
    open={Boolean(anchorEl)}
    onClose={handleCloseUserMenu}
    sx={{ ...styles.VDG_colActionIconMenu }}
  >
    {anchorEl?.row
      ? getMenuItems(anchorEl.row).map((menuItem, index) => {
          const row = anchorEl.row;
          const isDisabled =
            typeof menuItem.disabled === "function" && row
              ? menuItem.disabled(row)
              : menuItem.disabled || false;

          return (
            <MenuItem
              key={index}
              onClick={() => {
                if (!isDisabled) handleMenuItemClick(menuItem, row);
              }}
              sx={{ ...styles.VDG_colActionIcon }}
              disabled={isDisabled}
            >
              {menuItem.icon}
              <span className="menuItemText">{menuItem.text}</span>
            </MenuItem>
          );
        })
      : null}
  </Menu>
)}

      </Paper>
    </>
  );
}

ViewDataGridGrnApprover.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
 clickableGrn: PropTypes.bool,
  onGrnClick: PropTypes.func,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.node,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    })
  ),
  tabletitle: PropTypes.string,
  gridid: PropTypes.string,
  getMenuItems: PropTypes.func,
  displayActions: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  grnStatusTypes: PropTypes.array,
  grnReturnStatusTypes: PropTypes.array,
  selectionModel: PropTypes.array,
  sx: PropTypes.object,
  loading: PropTypes.bool,
  initialSortModel: PropTypes.array,
  showSkeletonWhenEmpty: PropTypes.bool,
};

export default ViewDataGridGrnApprover;
